import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { PrintCoordinator } from "./PrintCoordinator.ts";
import { runPrint } from "./runPrint.ts";

interface PrintContextValue {
    isPreparing: boolean;
    coordinator: PrintCoordinator;
    beginPreparation: () => void;
    endPreparation: () => void;
}

const PrintContext = createContext<PrintContextValue>({
    isPreparing: false,
    coordinator: new PrintCoordinator(),
    beginPreparation: () => {},
    endPreparation: () => {}
});

export function usePrintContext() {
    return use(PrintContext);
}

export interface PrintProviderProps {
    children: ReactNode;
}

export function PrintProvider({ children }: PrintProviderProps) {
    const [isPreparing, setIsPreparing] = useState(false);
    const coordinator = useMemo(() => new PrintCoordinator(), []);

    const beginPreparation = useCallback(() => {
        coordinator.beginPreparation();
        setIsPreparing(true);
    }, [coordinator]);

    const endPreparation = useCallback(() => setIsPreparing(false), []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey || event.key.toLowerCase() !== "p") {
                return;
            }

            event.preventDefault();

            void runPrint({ isPreparing, coordinator, beginPreparation, endPreparation });
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isPreparing, coordinator, beginPreparation, endPreparation]);

    const value = useMemo(() => ({ isPreparing, coordinator, beginPreparation, endPreparation }), [isPreparing, coordinator, beginPreparation, endPreparation]);

    return <PrintContext value={value}>{children}</PrintContext>;
}
